/**
 * Labels for the permission grid.
 *
 * The grid used to render `t(key) || permission.description`, which never once
 * reached the description. i18n.js sets a `parseMissingKeyHandler` that
 * humanises an unknown key, so a missing `deliveryVerify` comes back as the
 * truthy string "Delivery Verify" and the `||` can never fire — an English
 * camelCase echo, shown to an Arabic user, that looks like a translation.
 *
 * `i18next.exists()` is the only thing that distinguishes a real translation
 * from the handler's echo, so every label goes through it. Verified against the
 * real i18n setup, not assumed: t('deliveryVerify', {defaultValue}) ALSO returns
 * "Delivery Verify", so the usual defaultValue fix would have been just as inert.
 *
 * The i18n INSTANCE is passed in rather than read off `t`: useTranslation's `t`
 * has no `.i18n` property (checked — it is undefined), so reaching for it would
 * have made exists() falsy everywhere and quietly untranslated every label.
 */

/**
 * The i18n key the catalog implies for a permission: resource + PascalCase
 * action, e.g. {resource:'delivery', action:'verify'} -> 'deliveryVerify'.
 *
 * The seven HR permissions carry a DOTTED action ('payroll.read'), so their key
 * contains a dot. i18next reads a dot as a key path, but it resolves a flat key
 * spelled with dots too — confirmed against the real initialiser — so the keys
 * are written flat, exactly as this builds them.
 */
export const permissionKey = ({ resource, action }) =>
  `${resource}${action.charAt(0).toUpperCase()}${action.slice(1)}`;

/** The heading key for a catalog category: 'Job Management' -> 'JobManagement'. */
export const categoryKey = (category) => String(category).replace(/\s+/g, '');

/**
 * A permission's description in the active language.
 *
 * Falls back to the catalog's own English description, which is a real sentence
 * a user can act on, rather than to the humanised key echo.
 */
export const permissionLabel = (t, i18n, permission) => {
  const key = permissionKey(permission);
  return i18n?.exists(key) ? t(key) : permission.description || '';
};

/**
 * A category heading in the active language.
 *
 * A heading has no description to fall back to, so an untranslated one falls
 * back to the catalog's own words ('Job Management') — English, but real words
 * rather than 'JobManagement' run together.
 */
export const categoryLabel = (t, i18n, category) => {
  const key = categoryKey(category);
  return i18n?.exists(key) ? t(key) : category;
};
